// End of day hub for managers and owners: tonight's three steps in one place.
import { motion } from 'motion/react';
import { CheckCircle2, FileText, Lock, PenLine } from 'lucide-react';
import { useState } from 'react';
import { useAppColors } from '@/src/lib/theme';
import { eodChecks } from '@/src/lib/eod';
import { eodSteps, nextStep, type EodStepId } from '@/src/lib/eodSteps';
import { flagByKey, longDay, timeOf } from '@/src/lib/checkin';
import { openEodPdf } from '@/src/lib/eodReportFile';
import type { NightCheckin, NightNumbers, NightPerson } from '@/src/hooks/useEodNight';
import { Avatar, Chip, DateStepper, Panel, StepBar } from './eod-ui';
import { useMyLanguage } from '@/src/hooks/useMyLanguage';
import { useCheckinTranslations } from '@/src/hooks/useCheckinTranslations';

interface Props {
  userId: string;
  date: string;
  maxDate: string;
  onDate: (d: string) => void;
  tz: string | null;
  checkins: NightCheckin[];
  people: NightPerson[];
  numbers: NightNumbers | null;
  loading: boolean;
  onOpen: (view: 'team' | 'numbers' | 'checkin', step?: EodStepId) => void;
}

const money = (n: number | null | undefined) =>
  n == null ? '—' : `${n < 0 ? '−' : ''}$${Math.abs(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function EodHub({ userId, date, maxDate, onDate, tz, checkins, people, numbers, loading, onOpen }: Props) {
  const c = useAppColors();
  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const { language } = useMyLanguage();
  const { translations } = useCheckinTranslations(checkins, language);
  const isTonight = date === maxDate;

  // Step 1: check-ins
  const inIds = new Set(checkins.map((r) => r.userId));
  // Only people with an OpsAlo login can send a check-in.
  const canSend = people.filter((p) => p.onOpsAlo !== false);
  const missing = canSend.filter((p) => !inIds.has(p.userId));
  const expected = new Set([...canSend.map((p) => p.userId), ...inIds]).size;
  const allIn = expected > 0 && missing.length === 0;
  const meIn = inIds.has(userId);
  const meRostered = people.some((p) => p.userId === userId);

  // Step 2: numbers
  const submitted = numbers?.status === 'submitted';
  const steps = numbers?.data && numbers.calc ? eodSteps(numbers.data, numbers.calc, { submitted, cfg: numbers.config }) : null;
  const doneCount = steps ? steps.filter((s) => s.done && s.id !== 'submit').length : 0;
  const stepTotal = (steps?.length ?? 1) - 1;
  const nxt = steps ? nextStep(steps) : null;
  const started = !!numbers?.data;
  const imported = numbers?.status === 'imported' && !started;

  // Step 3: close
  const checks = numbers?.data && numbers.calc ? eodChecks(numbers.data, numbers.calc) : [];
  const ready = checks.length > 0 && checks.every((x) => x.ok);
  const k = numbers?.calc ?? null;

  // Which card to draw attention to.
  const focus: 1 | 2 | 3 = submitted ? 3 : ready ? 3 : started || allIn ? 2 : 1;

  const flagged = checkins.flatMap((r) => r.checkin.flags.map((f) => ({ r, f, note: (translations[r.id]?.from ? translations[r.id].checkin.flagNotes[f] : null) ?? r.checkin.flagNotes[f] ?? '' })));

  const label = (t: string) => <span className="text-[13px] font-semibold" style={{ color: c.textDim }}>{t}</span>;
  const num = (n: number, on: boolean) => (
    <span className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[13px] font-bold shrink-0"
      style={on ? { background: c.accent, color: '#fff' } : { background: c.accentBg, color: c.accentSoft }}>{n}</span>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="px-4 sm:px-8 py-6 max-w-5xl mx-auto w-full space-y-6 pb-24">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1 min-w-0">
          <p className="text-[12px] font-semibold uppercase tracking-[0.08em]" style={{ color: c.textDim }}>End of day</p>
          <h1 className="text-[28px] sm:text-[32px] font-bold tracking-tight leading-tight" style={{ color: c.text }}>
            {isTonight ? `Tonight, ${longDay(date)}` : longDay(date)}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateStepper date={date} max={maxDate} onChange={onDate} />
          {isTonight && (meRostered || !meIn) && (
            <button onClick={() => onOpen('checkin')} className="h-10 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2"
              style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
              {meIn ? <CheckCircle2 size={15} style={{ color: c.accentSoft }} /> : <PenLine size={15} />} My check-in
            </button>
          )}
        </div>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        {/* 1 · Check-ins */}
        <Panel strong={focus === 1} className={`p-5 flex flex-col gap-4 ${focus === 1 ? '' : ''}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2.5">{num(1, focus === 1)}{label('Staff check-ins')}</span>
            {expected > 0 && <Chip tone={allIn ? 'green' : 'grey'}>{checkins.length} of {expected} in</Chip>}
          </div>
          <div className="space-y-1">
            <p className="text-xl font-bold" style={{ color: c.text }}>
              {loading ? '…' : expected === 0 ? 'No one on the roster' : allIn ? 'Everyone is in' : `${missing.length} still to send`}
            </p>
            <p className="text-sm" style={{ color: c.textDim }}>
              {expected === 0 ? 'Check-ins still show up here as people send them.'
                : allIn ? `${flagged.length ? `${flagged.length} thing${flagged.length === 1 ? '' : 's'} flagged.` : 'Nothing flagged.'}`
                : `${missing.slice(0, 3).map((p) => p.name.split(' ')[0]).join(', ')}${missing.length > 3 ? ` and ${missing.length - 3} more` : ''}${isTonight ? '' : " didn't send one"}.`}
            </p>
          </div>
          {expected > 0 && (
            <div className="flex items-center flex-wrap gap-y-1" aria-label={`${checkins.length} checked in, ${missing.length} to go`}>
              {checkins.slice(0, 8).map((r, i) => <span key={r.id} style={{ marginLeft: i ? -8 : 0, borderRadius: 999, boxShadow: `0 0 0 2px ${c.bgElevated}` }}><Avatar name={r.name} size={32} /></span>)}
              {missing.slice(0, 6).map((p, i) => <span key={p.userId} style={{ marginLeft: i || checkins.length ? 4 : 0 }}><Avatar name={p.name} size={32} pending /></span>)}
            </div>
          )}
          <button onClick={() => onOpen('team')} className="mt-auto h-11 rounded-xl text-sm font-semibold"
            style={focus === 1 ? { background: c.accent, color: '#fff' } : { background: 'transparent', border: `1px solid ${c.borderStrong}`, color: c.text }}>
            See team check-ins
          </button>
        </Panel>

        {/* 2 · Nightly numbers */}
        <Panel strong={focus === 2} className="p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2.5">{num(2, focus === 2)}{label('Nightly numbers')}</span>
            <Chip tone={submitted ? 'green' : started ? 'amber' : 'grey'}>{submitted ? 'Submitted' : started ? 'In progress' : imported ? 'Imported' : 'Not started'}</Chip>
          </div>
          <div className="space-y-1">
            <p className="text-xl font-bold" style={{ color: c.text }}>
              {loading ? '…' : submitted ? 'All done' : !started ? 'Start with the printouts' : nxt && nxt.id !== 'submit' ? `Next: ${nxt.label.toLowerCase()}` : 'Ready to check and submit'}
            </p>
            <p className="text-sm" style={{ color: c.textDim }}>
              {submitted ? `${money(k?.total ?? numbers?.total)} · ${k?.covers ?? numbers?.covers ?? '—'} covers`
                : started ? `${doneCount} of ${stepTotal} steps done.` : imported ? 'Sales came from the CSV import. Add the rest.' : 'Photo the end-of-day printout and OpsAlo reads it.'}
            </p>
          </div>
          <StepBar total={stepTotal} done={(steps ?? []).filter((s) => s.id !== 'submit').map((s) => s.done)} />
          <button onClick={() => onOpen('numbers', submitted ? 'submit' : nxt?.id)} className="mt-auto h-11 rounded-xl text-sm font-semibold"
            style={focus === 2 ? { background: c.accent, color: '#fff' } : { background: 'transparent', border: `1px solid ${c.borderStrong}`, color: c.text }}>
            {submitted ? 'View numbers' : started ? 'Continue' : 'Start'}
          </button>
        </Panel>

        {/* 3 · Close */}
        <Panel strong={focus === 3} dashed={!ready && !submitted} className="p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2.5">{num(3, focus === 3)}{label('Close the day')}</span>
          </div>
          <div className="space-y-1">
            <p className="text-xl font-bold" style={{ color: submitted || ready ? c.text : c.textDim }}>
              {submitted ? 'Report sent' : ready ? 'Ready to submit' : 'Submit and send the PDF'}
            </p>
            <p className="text-sm" style={{ color: c.textDim }}>
              {submitted ? `${numbers?.submittedBy ? `By ${numbers.submittedBy}` : 'Submitted'}${numbers?.submittedAt ? ` at ${timeOf(numbers.submittedAt, tz)}` : ''}. Owners and managers have it.`
                : ready ? 'Everything balances. Owners and managers get the PDF when you submit.'
                : 'Opens when the numbers balance. Owners get the report straight away.'}
            </p>
          </div>
          {submitted ? (
            <div className="mt-auto flex gap-2">
              {numbers?.pdfPath && (
                <button onClick={async () => setPdfErr(await openEodPdf(numbers.pdfPath!))} className="flex-1 h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" style={{ background: c.accent, color: '#fff' }}>
                  <FileText size={15} /> Open PDF
                </button>
              )}
            </div>
          ) : ready ? (
            <button onClick={() => onOpen('numbers', 'submit')} className="mt-auto h-11 rounded-xl text-sm font-semibold" style={{ background: c.accent, color: '#fff' }}>Review and submit</button>
          ) : (
            <button disabled className="mt-auto h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" style={{ background: c.iconBg, color: c.textDim }}>
              <Lock size={14} /> Not ready yet
            </button>
          )}
          {pdfErr && <p className="text-xs" style={{ color: c.danger }}>{pdfErr}</p>}
        </Panel>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Panel className="p-5">
          <h2 className="text-base font-bold mb-2" style={{ color: c.text }}>Flagged by the team{isTonight ? ' tonight' : ''}</h2>
          {flagged.length === 0 ? (
            <p className="text-sm py-3" style={{ color: c.textDim }}>{checkins.length ? 'Nothing flagged so far.' : 'Flags from check-ins show up here.'}</p>
          ) : flagged.slice(0, 6).map(({ r, f, note }, i) => {
            const def = flagByKey(f);
            return (
              <div key={`${r.id}-${f}`} className="flex gap-3 items-start py-3" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                <span className="shrink-0 pt-0.5"><Chip tone={def?.tone ?? 'grey'}>{def?.short ?? f}</Chip></span>
                <div className="min-w-0">
                  <p className="text-sm" style={{ color: c.text }}>{note || def?.label}</p>
                  <p className="text-[12px]" style={{ color: c.textDim }}>{r.name.split(' ')[0]}{r.position ? ` · ${r.position}` : ''}</p>
                </div>
              </div>
            );
          })}
          {flagged.length > 6 && <button onClick={() => onOpen('team')} className="text-[13px] font-semibold mt-1" style={{ color: c.accentSoft }}>See all {flagged.length}</button>}
        </Panel>

        <Panel className="p-5">
          <h2 className="text-base font-bold mb-3" style={{ color: c.text }}>{isTonight ? 'Tonight so far' : 'The numbers'}</h2>
          <div className="grid grid-cols-2 gap-3">
            <Tile label="Sales, inc GST" value={money(k?.total ?? numbers?.total)} />
            <Tile label="Covers" value={String(k?.covers ?? numbers?.covers ?? '—')} />
            <Tile label="Cash variance" value={k?.cashVariance == null ? (numbers?.data?.cash?.noCash ? 'No cash' : 'Not counted') : Math.abs(k.cashVariance) < 0.01 ? '$0.00' : money(k.cashVariance)}
              tone={k?.cashVariance != null && Math.abs(k.cashVariance) >= 0.01 ? 'bad' : k?.cashVariance != null ? 'good' : 'muted'} />
            <Tile label="Labour" value={k?.labourPct != null ? `${k.labourPct}%` : '—'} tone={k?.labourPct == null ? 'muted' : undefined} />
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}

function Tile({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' | 'muted' }) {
  const c = useAppColors();
  const color = tone === 'bad' ? c.danger : tone === 'good' ? c.accentSoft : tone === 'muted' ? c.textDim : c.text;
  return (
    <div className="rounded-2xl p-3.5" style={{ background: c.iconBg }}>
      <p className="text-[12px]" style={{ color: c.textDim }}>{label}</p>
      <p className="text-xl font-bold mt-0.5 tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}
